import { Link } from 'wasp/client/router';
import { routes } from 'wasp/client/router';

interface TopicCardProps {
  topic: {
    slug: string;
    name: string;
    description?: string | null;
    imageUrl?: string | null;
    topicType: {
      label: string;
      icon?: string | null;
    };
    parentTopic?: {
      name: string;
    } | null;
    subTopics?: any[];
    _count?: {
      topicServices?: number;
      topicOccasions?: number;
    };
  };
}

export default function TopicCard({ topic }: TopicCardProps) {
  const serviceCount = topic._count?.topicServices || 0;
  const occasionCount = topic._count?.topicOccasions || 0;
  const subTopicCount = topic.subTopics?.length || 0;

  return (
    <Link
      to={routes.TopicDetailRoute.build({ params: { slug: topic.slug } }) as any}
      className="block group"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden border border-gray-200 dark:border-gray-700">
        {/* Image Section */}
        {topic.imageUrl && (
          <div className="h-48 overflow-hidden">
            <img
              src={topic.imageUrl}
              alt={topic.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
            />
          </div>
        )}

        {/* Content Section */}
        <div className="p-5">
          {/* Topic Type Badge */}
          <div className="flex items-center gap-2 mb-3">
            {topic.topicType.icon && (
              <span className="text-lg">{topic.topicType.icon}</span>
            )}
            <span className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide">
              {topic.topicType.label}
            </span>
          </div>

          {/* Topic Name */}
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
            {topic.name}
          </h3>

          {/* Parent Topic */}
          {topic.parentTopic && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              Under: {topic.parentTopic.name}
            </p>
          )}

          {/* Description */}
          {topic.description && (
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-3">
              {topic.description}
            </p>
          )}

          {/* Stats */}
          <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            {serviceCount > 0 && (
              <div className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>{serviceCount} service{serviceCount !== 1 ? 's' : ''}</span>
              </div>
            )}

            {occasionCount > 0 && (
              <div className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>{occasionCount} occasion{occasionCount !== 1 ? 's' : ''}</span>
              </div>
            )}

            {subTopicCount > 0 && (
              <div className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                <span>{subTopicCount} sub-topic{subTopicCount !== 1 ? 's' : ''}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}



